import { useMemo, useState } from 'react';

import { useAuth, useRecruiterId } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { recruiterAgentsBackrefRepoFor } from '../lib/repositories/recruiters-repo.js';
import { money, type RecruiterAgentBackref, type RecruiterStatus } from '@kilat/shared';

import { EmptyState, PageShell, formatDate, tsMs } from './recruiter-home.js';
import { Skeleton as UiSkeleton } from '@kilat/ui';

/** Radius tokens, hoisted out of JSX so `no-literal-string` sees no literal. */
const RADIUS_LG = 'var(--radius-lg)';

/**
 * /recruiter/agents — the downstream agents I signed up.
 *
 * Reads `recruiters/{recruiterId}/agents/{agentId}` back-refs (written
 * by `agentBootstrap` Sprint 84, patched by `commissionAccrue` Sprint 83
 * on each paid downstream invoice). Sortable by signup date / earnings.
 * Search by name / email / phone.
 *
 * Recruiters can't reach `agents/{aid}` directly (firestore.rules
 * scopes recruiter reads to their own subtree), so this view shows
 * what the back-ref snapshots: name, contact, currentAgentStatus,
 * first/lastDownstreamPaidAt, lifetimeRecruiterEarningsSen.
 */

type SortKey = 'recent' | 'top_earner';

// Enum key lists as ALL-CAPS module consts so their string members aren't bare
// JSX literals (which the i18n lint would otherwise flag) — these are status/
// sort tokens, not user copy; their visible labels resolve via `labelForStatus`
// / i18n keys at render.
const SORT_KEYS = ['recent', 'top_earner'] as const;
const STATUS_KEYS = ['all', 'active', 'pending_kyc', 'suspended', 'terminated'] as const;

export function RecruiterAgentsPage() {
  const { user, loading: authLoading } = useAuth();
  const recruiterId = useRecruiterId();
  const t = useI18n();
  const brand = usePlatformBrand();
  // Listener-level failure + retry. Without onError a denied/dropped read left
  // this list loading forever with the error branch unreachable
  // ([[feedback-no-dead-ends]]).
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const subscribe = useMemo(() => {
    if (!recruiterId) return null;
    const repo = recruiterAgentsBackrefRepoFor(recruiterId);
    return (cb: (vs: RecruiterAgentBackref[]) => void) =>
      repo.listSnapshot(
        (v) => {
          setLoadError(null);
          cb(v);
        },
        (err) => setLoadError(err),
      );
  }, [recruiterId]);
  const { data: rows, loading } = useTrackedSnapshot<RecruiterAgentBackref[]>(
    ['recruiter-agents-list', recruiterId, retryNonce],
    subscribe,
  );

  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>('recent');
  const [statusFilter, setStatusFilter] = useState<'all' | RecruiterStatus>('all');

  if (authLoading) {
    return (
      <PageShell>
        <Skeleton />
      </PageShell>
    );
  }
  if (!user) {
    return (
      <PageShell>
        <EmptyState
          title={t.t('agent.payouts.signedOutTitle')}
          body={t.t('agent.recruiterAgents.signedOutBody')}
        />
      </PageShell>
    );
  }
  if (!recruiterId) {
    return (
      <PageShell>
        <EmptyState
          title={t.t('agent.payouts.notRecruiterTitle')}
          body={t.t('agent.recruiterAgents.notRecruiterBody')}
          ctaLabel={t.t('agent.payouts.notRecruiterCta')}
          ctaHref={`https://${brand.domains.marketing}/become-a-recruiter`}
        />
      </PageShell>
    );
  }
  if (loadError) {
    return (
      <PageShell>
        <EmptyState
          title={t.t('agent.loadError.recruiterAgentsTitle')}
          body={t.t('agent.loadError.body')}
          onRetry={() => {
            setLoadError(null);
            setRetryNonce((n) => n + 1);
          }}
          retryLabel={t.t('agent.loadError.retry')}
        />
      </PageShell>
    );
  }
  if (loading || rows === null) {
    return (
      <PageShell>
        <Skeleton />
      </PageShell>
    );
  }
  if (rows.length === 0) {
    return (
      <PageShell>
        <Header count={0} />
        <EmptyState
          title={t.t('agent.recruiterAgents.emptyTitle')}
          body={t.t('agent.recruiterAgents.emptyBody')}
        />
      </PageShell>
    );
  }

  const filtered = filterAndSort(rows, query, sort, statusFilter);
  return (
    <PageShell>
      <Header count={rows.length} />
      <Toolbar
        query={query}
        onQuery={setQuery}
        sort={sort}
        onSort={setSort}
        statusFilter={statusFilter}
        onStatusFilter={setStatusFilter}
      />
      {filtered.length === 0 ? (
        <EmptyState
          title={t.t('agent.recruiterAgents.noMatchTitle')}
          body={t.t('agent.recruiterAgents.noMatchBody')}
        />
      ) : (
        <ul
          className="ag-grid"
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'grid',
            gap: 'var(--space-3)',
          }}
        >
          {filtered.map((row) => (
            <li key={row.agentId ?? row.agentEmail ?? row.agentPhone ?? Math.random()}>
              <AgentRow row={row} />
            </li>
          ))}
        </ul>
      )}
    </PageShell>
  );
}

function Header({ count }: { readonly count: number }) {
  const t = useI18n();
  return (
    <header>
      <h1 style={{ margin: 0, fontSize: 'var(--text-h1)', fontWeight: 600 }}>
        {t.t('agent.recruiterAgents.title')}
      </h1>
      <p
        style={{
          margin: 'var(--space-2) 0 0 0',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
        }}
      >
        {count === 0
          ? t.t('agent.recruiterAgents.teamEmpty')
          : t.t(
              count === 1
                ? 'agent.recruiterAgents.teamCountOne'
                : 'agent.recruiterAgents.teamCountOther',
              { count },
            )}
      </p>
    </header>
  );
}

function Toolbar({
  query,
  onQuery,
  sort,
  onSort,
  statusFilter,
  onStatusFilter,
}: {
  readonly query: string;
  readonly onQuery: (s: string) => void;
  readonly sort: SortKey;
  readonly onSort: (s: SortKey) => void;
  readonly statusFilter: 'all' | RecruiterStatus;
  readonly onStatusFilter: (s: 'all' | RecruiterStatus) => void;
}) {
  const t = useI18n();
  return (
    <section
      style={{
        display: 'grid',
        gap: 'var(--space-3)',
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4)',
        boxShadow: 'var(--shadow-1)',
      }}
    >
      <label style={{ display: 'grid', gap: 'var(--space-2)' }}>
        <span style={fieldLabel}>{t.t('agent.recruiterAgents.searchLabel')}</span>
        <input
          type="search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder={t.t('agent.recruiterAgents.searchPlaceholder')}
          autoComplete="off"
          style={inputStyle}
        />
      </label>
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        {SORT_KEYS.map((opt) => (
          <button
            key={opt}
            type="button"
            aria-pressed={sort === opt}
            onClick={() => onSort(opt)}
            style={chipStyle(sort === opt)}
          >
            {opt === 'recent'
              ? t.t('agent.recruiterAgents.sortRecent')
              : t.t('agent.recruiterAgents.sortTopEarner')}
          </button>
        ))}
        <span
          aria-hidden
          style={{
            width: 1,
            background: 'var(--color-border)',
            alignSelf: 'stretch',
            margin: '0 var(--space-1)',
          }}
        />
        {STATUS_KEYS.map((s) => (
          <button
            key={s}
            type="button"
            aria-pressed={statusFilter === s}
            onClick={() => onStatusFilter(s)}
            style={chipStyle(statusFilter === s)}
          >
            {s === 'all'
              ? t.t('agent.recruiterAgents.filterAllStatus')
              : labelForStatus(s as RecruiterStatus)}
          </button>
        ))}
      </div>
    </section>
  );
}

function AgentRow({ row }: { readonly row: RecruiterAgentBackref }) {
  const t = useI18n();
  const earnings = row.lifetimeRecruiterEarningsSen ?? 0;
  const status = row.currentAgentStatus ?? 'active';
  const firstPaid = row.firstDownstreamPaidAt ? tsMs(row.firstDownstreamPaidAt) : 0;
  const lastPaid = row.lastDownstreamPaidAt ? tsMs(row.lastDownstreamPaidAt) : 0;
  return (
    <article
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4)',
        boxShadow: 'var(--shadow-1)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 'var(--space-3)',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <strong style={{ display: 'block', fontSize: 'var(--text-body)' }}>
            {row.agentName ?? t.t('agent.recruiterAgents.unnamedAgent')}
          </strong>
          <span
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {row.agentEmail ?? '—'} · {row.agentPhone ?? '—'}
          </span>
        </div>
        <StatusPill status={status} />
      </header>
      <dl
        style={{
          margin: 0,
          display: 'grid',
          gap: 'var(--space-3) var(--space-4)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))',
        }}
      >
        <Stat
          label={t.t('agent.recruiterAgents.statRecruiterEarnings')}
          value={money.format(earnings)}
        />
        <Stat
          label={t.t('agent.recruiterAgents.statSignedUp')}
          value={formatDate(tsMs(row.signedUpAt))}
        />
        <Stat
          label={t.t('agent.recruiterAgents.statFirstPaid')}
          value={firstPaid ? formatDate(firstPaid) : t.t('agent.recruiterAgents.notYet')}
        />
        <Stat
          label={t.t('agent.recruiterAgents.statLastPaid')}
          value={lastPaid ? formatDate(lastPaid) : '—'}
        />
      </dl>
      {firstPaid === 0 ? (
        <p
          style={{
            margin: 0,
            padding: 'var(--space-2) var(--space-3)',
            background: 'rgba(245,158,11,0.08)',
            border: '1px solid rgba(245,158,11,0.22)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--text-meta)',
            color: '#92400e',
          }}
        >
          {t.t('agent.recruiterAgents.notPaidYet')}
        </p>
      ) : null}
    </article>
  );
}

function Stat({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div>
      <dt
        style={{
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </dt>
      <dd
        style={{
          margin: 'var(--space-1) 0 0 0',
          fontSize: 'var(--text-body)',
          fontWeight: 500,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </dd>
    </div>
  );
}

function StatusPill({ status }: { readonly status: RecruiterStatus }) {
  const pill = (() => {
    switch (status) {
      case 'active':
        return { bg: 'rgba(13,122,79,0.10)', fg: 'var(--color-primary)' };
      case 'pending_kyc':
        return { bg: 'rgba(245,158,11,0.10)', fg: '#b45309' };
      case 'suspended':
        return { bg: 'rgba(239,68,68,0.10)', fg: '#b91c1c' };
      case 'terminated':
        return { bg: 'var(--color-border)', fg: 'var(--color-on-surface-muted)' };
      case 'applied':
        return { bg: 'var(--tint-subtle)', fg: 'var(--color-on-surface-muted)' };
    }
  })();
  return (
    <span
      style={{
        padding: '4px 10px',
        borderRadius: 'var(--radius-pill)',
        background: pill.bg,
        color: pill.fg,
        fontSize: 'var(--text-meta)',
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {labelForStatus(status)}
    </span>
  );
}

function Skeleton() {
  // Shared shimmer from @kilat/ui, not a hand-rolled grey block.
  //
  // This app carried three separate local copies of this component, each
  // painting a static `var(--color-border)` rectangle — so its loading states
  // never shimmered, and [[feedback-unified-packages-ui]] ("extract before the
  // second copy lands") had been breached twice over in one app.
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      aria-busy="true"
    >
      <UiSkeleton count={3} height={120} radius={RADIUS_LG} />
    </div>
  );
}

function filterAndSort(
  rows: readonly RecruiterAgentBackref[],
  rawQuery: string,
  sort: SortKey,
  statusFilter: 'all' | RecruiterStatus,
): RecruiterAgentBackref[] {
  const q = rawQuery.trim().toLowerCase();
  const filtered = rows.filter((row) => {
    if (statusFilter !== 'all' && (row.currentAgentStatus ?? 'active') !== statusFilter) {
      return false;
    }
    if (!q) return true;
    return (
      (row.agentName ?? '').toLowerCase().includes(q) ||
      (row.agentEmail ?? '').toLowerCase().includes(q) ||
      (row.agentPhone ?? '').toLowerCase().includes(q)
    );
  });
  const sorted = [...filtered];
  if (sort === 'recent') {
    sorted.sort((a, b) => tsMs(b.signedUpAt) - tsMs(a.signedUpAt));
  } else {
    sorted.sort(
      (a, b) => (b.lifetimeRecruiterEarningsSen ?? 0) - (a.lifetimeRecruiterEarningsSen ?? 0),
    );
  }
  return sorted;
}

function labelForStatus(s: RecruiterStatus): string {
  switch (s) {
    case 'active':
      return 'Active';
    case 'pending_kyc':
      return 'Pending KYC';
    case 'suspended':
      return 'Suspended';
    case 'terminated':
      return 'Terminated';
    case 'applied':
      return 'Applied';
  }
}

const fieldLabel: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};

const inputStyle: React.CSSProperties = {
  padding: '12px 14px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--color-border-strong)',
  fontSize: 'var(--text-body)',
  fontFamily: 'inherit',
  background: 'var(--color-surface)',
  color: 'var(--color-on-surface)',
  width: '100%',
};

function chipStyle(selected: boolean): React.CSSProperties {
  return {
    padding: '6px 12px',
    borderRadius: 'var(--radius-pill)',
    border: `1px solid ${selected ? 'var(--color-primary)' : 'var(--color-border-strong)'}`,
    background: selected ? 'var(--color-primary)' : 'transparent',
    color: selected ? 'var(--color-primary-fg)' : 'var(--color-on-surface)',
    fontSize: 'var(--text-meta)',
    fontWeight: 500,
    cursor: 'pointer',
  };
}
